import React from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getInvoicesId, postInvoicesIdSync } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Invoice } from '../api/generated/model';
import InvoiceDetailContent, { qboInvoiceUrl } from './InvoiceDetailContent';
import { CopyButton } from './common/Copyable';

interface InvoiceDetailModalProps {
  show: boolean;
  onHide: () => void;
  invoice?: Invoice | null;
  invoiceId?: string | null;
  onInvoiceUpdated?: (invoice: Invoice) => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({ show, onHide, invoice: invoiceProp, invoiceId, onInvoiceUpdated }) => {
  const queryClient = useQueryClient();
  const { data: fetchedInvoice, isLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoicesId(invoiceId!),
    enabled: show && !!invoiceId && !invoiceProp,
  });

  // Local copy of the invoice post-resync. Takes priority over the prop
  // so the modal can self-refresh even when the caller passed a static
  // invoiceProp (in which case useQuery is disabled and invalidate
  // queries doesn't trigger a refetch).
  const [localInvoice, setLocalInvoice] = React.useState<Invoice | null>(null);
  // Drop the local override whenever the inputs change so the modal
  // doesn't keep showing a stale post-resync copy of a different invoice
  // after the caller switches to a new id/prop.
  React.useEffect(() => { setLocalInvoice(null); }, [invoiceId, invoiceProp]);

  const invoice = localInvoice || invoiceProp || fetchedInvoice || null;

  // Self-update on toggle: every approval / delivery patch comes back
  // with the latest invoice, but the modal's own copy only refreshes
  // if (a) the caller is tracking state via invoice prop and passes
  // onInvoiceUpdated to swap it, or (b) we have an active fetch under
  // ['invoice', invoiceId] whose cache we update. Two of four call
  // sites today (RemittancePanel, PaymentDetailModal) don't pass the
  // callback, so without this self-heal the toggles render stale and
  // the operator thinks nothing happened. Always-update keeps every
  // path working without each caller having to remember the wire-up.
  const handleInvoiceUpdated = React.useCallback((updated: Invoice) => {
    const id = invoiceId || updated?.id;
    if (id) {
      queryClient.setQueryData(['invoice', id], updated);
    }
    onInvoiceUpdated?.(updated);
  }, [invoiceId, queryClient, onInvoiceUpdated]);

  const [resyncing, setResyncing] = React.useState(false);
  const handleResync = React.useCallback(async () => {
    const id = (localInvoice || invoiceProp || fetchedInvoice)?.id || invoiceId;
    if (!id) return;
    setResyncing(true);
    try {
      await postInvoicesIdSync(id);
      // Pull the fresh copy and route it through handleInvoiceUpdated:
      // that updates the React Query cache (for any other consumer) and
      // fires onInvoiceUpdated so a parent tracking the invoice in state
      // swaps its prop. Stash it locally too — when the caller passed a
      // static invoiceProp without onInvoiceUpdated, neither the cache
      // nor the parent will refresh the modal's displayed copy, so the
      // local override is the only way the new lines show up here.
      const fresh = await getInvoicesId(id);
      setLocalInvoice(fresh as Invoice);
      handleInvoiceUpdated(fresh as Invoice);
    } finally {
      setResyncing(false);
    }
  }, [localInvoice, invoiceProp, fetchedInvoice, invoiceId, handleInvoiceUpdated]);

  if (!show) return null;

  if (isLoading && !invoice) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" className="me-2" />
          Loading invoice...
        </Modal.Body>
      </Modal>
    );
  }

  if (!invoice) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3 flex-wrap">
          <span>Invoice #{invoice.invoice_number}</span>
          {invoice.customer?.name && (
            <span className="text-muted fs-6">{invoice.customer.name}</span>
          )}
          {invoice.id && (
            <div className="d-flex gap-2 bg-header-control rounded px-2 py-1">
              <a
                href={`/invoices/${invoice.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Open
              </a>
              <CopyButton
                value={`${window.location.origin}/invoices/${invoice.id}`}
                variant="outline-primary"
                size="sm"
                title="Copy link to clipboard"
              >
                <i className="bi bi-link-45deg me-1"></i>
                Copy Link
              </CopyButton>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleResync}
                disabled={resyncing}
                title="Refetch this invoice (and its linked payments) from QuickBooks and re-run the auto-linker"
                className="d-inline-flex align-items-center gap-1"
              >
                {resyncing
                  ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /><span>Resyncing…</span></>
                  : <><i className="bi bi-arrow-clockwise" /><span>Resync</span></>}
              </Button>
              {qboInvoiceUrl(invoice) && (
                <a
                  href={qboInvoiceUrl(invoice)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  QuickBooks
                </a>
              )}
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3 d-flex flex-column" style={{ minHeight: 0 }}>
        <InvoiceDetailContent invoice={invoice} onInvoiceUpdated={handleInvoiceUpdated} />
      </Modal.Body>
    </Modal>
  );
};

export default InvoiceDetailModal;
